/**
 * Hero Parallax Controller
 * -------------------------------------------------------------
 * Powers the `parallax_style` option on the Hero section.
 * Reads data-attributes set by the section liquid and animates
 * the .hero__media elements accordingly.
 *
 * Styles supported:
 *  - classic   : traditional slow-scroll drift
 *  - zoom_in   : media scales UP as it enters/leaves the viewport
 *  - zoom_out  : media scales DOWN as it enters/leaves the viewport
 *  - depth     : two media layers move at different speeds (3D depth)
 *  - drift     : diagonal horizontal + vertical drift, layers move opposite directions
 *  - fade_rise : media rises and fades in as the section scrolls into view
 *  - tilt      : mouse/pointer-driven 3D tilt + shift (desktop-style interactive parallax)
 *
 * Respects prefers-reduced-motion and is safe to load multiple
 * times (e.g. Shopify theme editor section reloads).
 */
(function () {
  if (window.HeroParallax) return; // already loaded

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  var IS_MOBILE_QUERY = window.matchMedia('(max-width: 749px)');
  var instances = new WeakMap();

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function lerp(start, end, t) {
    return start + (end - start) * t;
  }

  function HeroParallaxInstance(el) {
    this.el = el;
    this.style = el.dataset.parallaxStyle || 'none';
    this.intensity = (parseFloat(el.dataset.parallaxIntensity) || 35) / 100;
    this.allowMobile = el.dataset.parallaxMobile === 'true';
    this.reverse = el.dataset.parallaxReverse === 'true' ? -1 : 1;
    this.mediaEls = Array.prototype.slice.call(el.querySelectorAll('.hero__media'));
    this.pointer = { x: 0, y: 0 };
    this.targetPointer = { x: 0, y: 0 };
    this.raf = null;
    this.bound = false;
    this.destroyed = false;

    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
    this.tick = this.tick.bind(this);

    this.setup();
  }

  HeroParallaxInstance.prototype.setup = function () {
    if (this.style === 'none' || REDUCED_MOTION.matches || !this.mediaEls.length) return;

    var self = this;
    this.observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            self.bind();
            self.startLoop();
          } else {
            self.stopLoop();
          }
        });
      },
      { rootMargin: '20% 0px 20% 0px', threshold: 0 }
    );
    this.observer.observe(this.el);
  };

  HeroParallaxInstance.prototype.bind = function () {
    if (this.bound) return;
    this.bound = true;
    if (this.style === 'tilt') {
      this.el.addEventListener('pointermove', this.onPointerMove);
      this.el.addEventListener('pointerleave', this.onPointerLeave);
    }
  };

  HeroParallaxInstance.prototype.unbind = function () {
    this.bound = false;
    this.el.removeEventListener('pointermove', this.onPointerMove);
    this.el.removeEventListener('pointerleave', this.onPointerLeave);
  };

  HeroParallaxInstance.prototype.onPointerMove = function (e) {
    var rect = this.el.getBoundingClientRect();
    this.targetPointer = {
      x: (e.clientX - rect.left) / rect.width - 0.5,
      y: (e.clientY - rect.top) / rect.height - 0.5
    };
  };

  HeroParallaxInstance.prototype.onPointerLeave = function () {
    this.targetPointer = { x: 0, y: 0 };
  };

  HeroParallaxInstance.prototype.startLoop = function () {
    if (this.raf) return;
    this.raf = requestAnimationFrame(this.tick);
  };

  HeroParallaxInstance.prototype.stopLoop = function () {
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
    this.unbind();
  };

  HeroParallaxInstance.prototype.isMobileBlocked = function () {
    return IS_MOBILE_QUERY.matches && !this.allowMobile;
  };

  HeroParallaxInstance.prototype.reset = function () {
    this.mediaEls.forEach(function (media) {
      media.style.transform = '';
      media.style.opacity = '';
    });
  };

  HeroParallaxInstance.prototype.tick = function () {
    if (this.destroyed) return;
    if (this.isMobileBlocked()) {
      this.reset();
    } else {
      this.update();
    }
    this.raf = requestAnimationFrame(this.tick);
  };

  // 0 -> 1 as the section travels from just-below-viewport to just-above-viewport
  HeroParallaxInstance.prototype.progress = function () {
    var rect = this.el.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var total = rect.height + vh;
    var distanceFromStart = vh - rect.top;
    return clamp(distanceFromStart / total, 0, 1);
  };

  HeroParallaxInstance.prototype.update = function () {
    var p = this.progress();
    var centered = (p - 0.5) * 2; // -1 -> 1, 0 at center of viewport pass
    var media = this.mediaEls;

    switch (this.style) {
      case 'classic': {
        var range = 70 * this.intensity;
        var offset = centered * range * this.reverse;
        media.forEach(function (m) {
          m.style.transform = 'translate3d(0, ' + offset.toFixed(2) + 'px, 0)';
        });
        break;
      }

      case 'zoom_in': {
        var scaleIn = 1 + (1 - Math.abs(centered)) * this.intensity * 0.35;
        media.forEach(function (m) {
          m.style.transform = 'scale(' + scaleIn.toFixed(4) + ')';
        });
        break;
      }

      case 'zoom_out': {
        var scaleOut = 1 + Math.abs(centered) * this.intensity * 0.35;
        media.forEach(function (m) {
          m.style.transform = 'scale(' + scaleOut.toFixed(4) + ')';
        });
        break;
      }

      case 'depth': {
        var rev = this.reverse;
        var intensity = this.intensity;
        media.forEach(function (m, i) {
          var layerFactor = i === 0 ? 0.55 : 1;
          var range = 90 * intensity * layerFactor;
          var offset = centered * range * rev;
          m.style.transform = 'translate3d(0, ' + offset.toFixed(2) + 'px, 0) scale(1.06)';
        });
        break;
      }

      case 'drift': {
        var driftRange = 55 * this.intensity;
        var driftOffset = centered * driftRange * this.reverse;
        var vertical = Math.abs(centered) * 18;
        media.forEach(function (m, i) {
          var dir = i % 2 === 0 ? 1 : -1;
          m.style.transform =
            'translate3d(' + (driftOffset * dir).toFixed(2) + 'px, ' + vertical.toFixed(2) + 'px, 0)';
        });
        break;
      }

      case 'fade_rise': {
        var rise = 45 * this.intensity;
        var opacity = clamp(0.35 + p * 0.65, 0.35, 1);
        var translate = (1 - p) * rise;
        media.forEach(function (m) {
          m.style.transform = 'translate3d(0, ' + translate.toFixed(2) + 'px, 0)';
          m.style.opacity = opacity.toFixed(2);
        });
        break;
      }

      case 'tilt': {
        this.pointer.x = lerp(this.pointer.x, this.targetPointer.x, 0.08);
        this.pointer.y = lerp(this.pointer.y, this.targetPointer.y, 0.08);
        var maxTilt = 12 * this.intensity;
        var maxShift = 22 * this.intensity;
        var rotateY = this.pointer.x * maxTilt;
        var rotateX = -this.pointer.y * maxTilt;
        var shiftX = this.pointer.x * maxShift;
        var shiftY = this.pointer.y * maxShift;
        media.forEach(function (m) {
          m.style.transform =
            'perspective(1000px) rotateX(' + rotateX.toFixed(2) + 'deg) rotateY(' + rotateY.toFixed(2) +
            'deg) translate3d(' + shiftX.toFixed(2) + 'px, ' + shiftY.toFixed(2) + 'px, 0) scale(1.06)';
        });
        break;
      }

      default:
        break;
    }
  };

  HeroParallaxInstance.prototype.destroy = function () {
    this.destroyed = true;
    this.stopLoop();
    if (this.observer) this.observer.disconnect();
    this.reset();
  };

  function initHero(el) {
    if (!el || instances.has(el)) return;
    var style = el.dataset.parallaxStyle;
    if (!style || style === 'none') return;
    instances.set(el, new HeroParallaxInstance(el));
  }

  function destroyHero(el) {
    var inst = instances.get(el);
    if (inst) {
      inst.destroy();
      instances.delete(el);
    }
  }

  function scan(root) {
    (root || document).querySelectorAll('.hero[data-parallax-style]').forEach(initHero);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan();
    });
  } else {
    scan();
  }

  // Theme editor (customizer) support — re-init/tear down as sections load/unload
  document.addEventListener('shopify:section:load', function (event) {
    initHero(event.target.querySelector('.hero[data-parallax-style]'));
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroyHero(event.target.querySelector('.hero[data-parallax-style]'));
  });

  document.addEventListener('shopify:section:select', function (event) {
    initHero(event.target.querySelector('.hero[data-parallax-style]'));
  });

  document.addEventListener('shopify:block:select', function () {
    scan();
  });

  window.HeroParallax = { scan: scan, init: initHero, destroy: destroyHero };
})();
